'use client'

import Icon from '@/components/Icon/Icon'
import { FF_DARK_LIGHT_MODE_TOGGLE } from '@/config/featureFlags'
import { FeatureFlagService } from '@/services/api/unleash/FeatureFlag.service'
import AppContext from '@/types/context'
import { clsx } from 'clsx'
import { useEffect, useState } from 'react'
import styles from './DarkLightModeToggle.module.scss'

type DarkLightModeToggleProps = {
  readonly className?: string
  context: AppContext
}

// TODO this component does not work correctly
// 1. preferred color scheme is applied only on client side, which leads to a flash of light mode on initial load for dark mode users (cookie vs localStorage)
// 2. preferred color scheme should be applied on layout level, so that it is applied on initial load and not overridden by cookie/localStorage value
// 3. concept is not user friendly, browser settings are given on client side, which could be another value than saved in cookie/localStorage - which could lead to a flash of light mode
const DarkLightModeToggle = ({ className, context }: DarkLightModeToggleProps) => {
  const [isDarkMode, setIsDarkMode] = useState(false)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    const savedMode = localStorage.getItem('dark-theme')
    const isFlagEnabled = FeatureFlagService.isFeatureFlagEnabled(context.featureFlags, FF_DARK_LIGHT_MODE_TOGGLE)

    if (
      !isFlagEnabled ||
      context.metadata?.model?.fields?.theme !== 'default' ||
      context.metadata?.model?.fields?.theme === undefined
    ) {
      return
    }

    if (savedMode !== null) {
      setIsDarkMode(savedMode === 'true')
    } else if (isFlagEnabled) {
      // Check system preference only if feature flag is enabled
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
      setIsDarkMode(prefersDark)
    }
    // If flag is disabled and no saved mode, default to light mode (false)
    setMounted(true)
  }, [context.featureFlags])

  useEffect(() => {
    if (!mounted) return

    const root = document.documentElement
    if (isDarkMode) {
      root.classList.add('dark-theme')
    } else {
      root.classList.remove('dark-theme')
    }
    localStorage.setItem('dark-theme', String(isDarkMode))
  }, [isDarkMode, mounted])

  const handleToggle = () => {
    setIsDarkMode(!isDarkMode)
  }

  if (!FeatureFlagService.isFeatureFlagEnabled(context.featureFlags, FF_DARK_LIGHT_MODE_TOGGLE)) {
    return null
  }

  if (!mounted) {
    return null
  }

  return (
    <button
      className={clsx(styles.darkLightModeToggle, className)}
      onClick={handleToggle}
      aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
      title={isDarkMode ? 'Light Mode' : 'Dark Mode'}
    >
      <Icon name="theme" size={18} />
    </button>
  )
}

export default DarkLightModeToggle
